import React from 'react'
import PropTypes from 'prop-types'
import SearchResultShape from 'components/search/prop_shapes/api_v2_supplier_result'
import { debounce } from 'lodash-es'
import { api } from 'components/api'
import { reviewMicroUpdatePath } from 'components/routes/path'
import SharedSupplierSearch from 'components/search/SharedSupplierSearch'
import SearchIcon from 'components/search/SearchIcon'

const RemoveButton = ({ onDismiss }) => (
  <button className='remove' aria-label='Remove' onClick={onDismiss}>
    <i className='fas fa-times' />
  </button>
)

RemoveButton.propTypes = {
  onDismiss: PropTypes.func
}

// Renders a styled dismissable supplier name to be used for the review
const SupplierNameForReview = ({ supplierName, onDismiss }) => (
  <div className='input-group-search'>
    <SearchIcon />
    <div className='input-group-field review-supplier-selected-name'>
      <div className='selection'>
        <div className='name'>
          {supplierName}
        </div>

        <RemoveButton onDismiss={onDismiss} />
      </div>
    </div>
  </div>
)

SupplierNameForReview.propTypes = {
  supplierName: PropTypes.string,
  onDismiss: PropTypes.func
}

const EmptyInput = () => null

// Clickable component for creating a new supplier for the review
const CreateSupplier = ({ supplierName, handleMouseDown, hidden }) => {
  if (hidden) { return null }
  return (
    <div
      className='create-supplier search-result clearfix'
      key='create-supplier'
      onMouseDown={handleMouseDown}
    >
      <div className='new-supplier-name'>{supplierName}</div>
      <div className='float-left'>Create a business with this name &rarr;</div>
      <div className='float-right'>
        <i className='fas fa-plus' />
      </div>
    </div>
  )
}

CreateSupplier.propTypes = {
  supplierName: PropTypes.string.isRequired,
  handleMouseDown: PropTypes.func.isRequired,
  hidden: PropTypes.bool
}

const SearchResult = ({ result, handleMouseDown }) => (
  <div
    className='search-result'
    key={result.id}
    id={`supplier-search-${result.id}`}
    onMouseDown={handleMouseDown}
  >
    <div className='name'>{result.name}</div>
    <div>{result.location}</div>
  </div>
)

SearchResult.propTypes = {
  result: SearchResultShape.isRequired,
  handleMouseDown: PropTypes.func
}

const SearchResults = ({
  searchResults, supplierName, handleSearchResult, handleSupplierName
}) => (
  <div className='search-results show'>
    <CreateSupplier
      supplierName={supplierName}
      handleMouseDown={handleSupplierName(supplierName)}
      hidden={(supplierName.length < 3)}
    />
    {
      (searchResults || []).map(result => (
        <SearchResult
          key={result.id}
          result={result}
          handleMouseDown={handleSearchResult(result)}
        />
      ))
    }
  </div>
)

SearchResults.propTypes = {
  searchResults: PropTypes.arrayOf(SearchResultShape).isRequired,
  supplierName: PropTypes.string.isRequired,
  handleSearchResult: PropTypes.func.isRequired,
  handleSupplierName: PropTypes.func.isRequired
}

// Higher order component to inject the appropriate handlers into the SearchResults component
// rendered by the SupplierSearchShared component
const searchResultsWithHandlers = (handleSearchResult, handleSupplierName) =>
  ({ searchResults, supplierName }) =>
    SearchResults({ searchResults, supplierName, handleSearchResult, handleSupplierName })

// poor man's enum
const SearchInputBehavior = {
  search: 'search',
  supplierName: 'supplierName',
  empty: 'empty'
}

class SearchSuppliers extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      supplierName: props.supplierName,
      supplierId: props.supplierId,
      searchInputBehavior: props.supplierName ? SearchInputBehavior.supplierName : SearchInputBehavior.search
    }
  }

  saveTemporarySupplierName = (supplierName) => {
    const data = { review: { flex: { temp_supplier_name: supplierName } } }
    this.microUpdate(data)
  }

  debouncedSaveTemporarySupplierName = debounce(this.saveTemporarySupplierName, 300)

  microUpdate = async (data) => {
    const res = await api.patchWithCsrf({ url: reviewMicroUpdatePath(this.props.reviewId), data })
    this.updateDOMForContractSelection(res.data)
  }

  // This method updates a hidden DOM element with the chosen supplier and the contract vehicles on which the supplier is listed.
  // The SelectContract component requires this information to function correctly i.e. allow the user to only select a contract on which the supplier is listed.
  updateDOMForContractSelection = (data) => {
    $('#chosen-supplier-data').data({ supplier: data.supplier, contractVehicles: data.contract_vehicles })
    $('#chosen-supplier-data').attr('supplier', data.supplier || 'none') // triggers MutationListener
  }

  handleSearchResult = result => event => this.selectSupplier(event, result.id, result.name)

  handleSupplierName = supplierName => {
    this.debouncedSaveTemporarySupplierName(supplierName)
    return event =>
      this.setState({ supplierName, searchInputBehavior: SearchInputBehavior.supplierName })
  }

  selectSupplier = (event, supplierId, supplierName) => {
    this.setState({
      supplierId,
      supplierName,
      searchInputBehavior: SearchInputBehavior.supplierName
    })

    const data = {
      review: {
        supplier_id: supplierId,
        flex: {
          temp_supplier_name: supplierName
        }
      }
    }
    this.microUpdate(data)
  }

  handleRemoveSupplier = () => {
    this.setState({ supplierId: '', searchInputBehavior: SearchInputBehavior.search })
    const data = { review: { supplier_id: '' } }
    this.microUpdate(data)
  }

  // Components that can be in the search input container
  // Note: Must be a function returning an object, a static object could result in stale state.
  searchInputOptions = () => ({
    [SearchInputBehavior.supplierName]: (
      <SupplierNameForReview
        supplierName={this.state.supplierName}
        onDismiss={this.handleRemoveSupplier}
      />
    ),
    [SearchInputBehavior.search]: (
      <SharedSupplierSearch
        SearchResultsComponent={searchResultsWithHandlers(
          this.handleSearchResult,
          this.handleSupplierName
        )}
      />
    ),
    [SearchInputBehavior.empty]: (
      <SharedSupplierSearch
        SearchResultsComponent={EmptyInput}
      />
    )
  })

  handleBlur = () => {
    if (this.state.searchInputBehavior !== SearchInputBehavior.supplierName) {
      this.setState({ searchInputBehavior: SearchInputBehavior.empty })
    }
  }

  handleFocus = () => {
    if (this.state.searchInputBehavior !== SearchInputBehavior.supplierName) {
      this.setState({ searchInputBehavior: SearchInputBehavior.search })
    }
  }

  render () {
    return (
      <div className='supplier-search'>
        <label
          className='select optional disabled sub-header'
          htmlFor='supplier-input'
        >
          Supplier Business Name
        </label>

        <div onBlur={this.handleBlur} onFocus={this.handleFocus}>
          {this.searchInputOptions()[this.state.searchInputBehavior]}
        </div>

        <input type='hidden' name='review[supplier_id]' value={this.state.supplierId} />
        <input type='hidden' name='review[flex][temp_supplier_name]' value={this.state.supplierName} />
      </div>
    )
  }
}

SearchSuppliers.propTypes = {
  supplierName: PropTypes.string,
  supplierId: PropTypes.string,
  reviewId: PropTypes.number
}

export default SearchSuppliers
